import React, { useState, useEffect } from 'react';
import { quizSteps } from './config/quizConfig.js';
import QuizFlow from './components/onboarding/QuizFlow.jsx';
import ResultsScreen from './components/onboarding/ResultsScreen.jsx';
import SocialProofMap from './components/onboarding/SocialProofMap.jsx';
import PlanPreviewScreen from './components/onboarding/PlanPreviewScreen.jsx';
import Paywall from './components/paywall/Paywall.jsx';
import UpsellModal from './components/paywall/UpsellModal.jsx';
import ExitOffer from './components/paywall/ExitOffer.jsx';
import CheckoutScreen from './components/paywall/CheckoutScreen.jsx';
import CheckoutSuccessScreen from './components/paywall/CheckoutSuccessScreen.jsx';
import DownloadScreen from './components/paywall/DownloadScreen.jsx';
import { track } from './lib/track.js';

const stepById = (id) => quizSteps.find((s) => s.id === id);

// Se o Stripe redirecionou de volta pra cá (?checkout=success/cancel),
// isso é lido UMA VEZ, logo na primeira renderização — antes de qualquer
// state resetar a URL.
function readCheckoutRedirect() {
  const params = new URLSearchParams(window.location.search);
  const checkout = params.get('checkout');
  if (checkout === 'success' || checkout === 'cancel') return checkout;
  return null;
}

// Guarda as respostas do quiz na sessão do navegador: ao ir pro Stripe e
// voltar (botão voltar / ?checkout=cancel) a página recarrega e o estado do
// React se perde — sem isso o e-mail some e o pagamento não consegue
// reiniciar. sessionStorage some quando a aba fecha (nada fica guardado).
const ANSWERS_KEY = 'fc_funnel_answers';
function saveAnswers(a) {
  try {
    sessionStorage.setItem(ANSWERS_KEY, JSON.stringify(a ?? {}));
  } catch {
    // sem storage (modo privado restrito) — segue sem guardar
  }
}
function loadAnswers() {
  try {
    return JSON.parse(sessionStorage.getItem(ANSWERS_KEY) || '{}') || {};
  } catch {
    return {};
  }
}

// Guarda em QUE TELA a pessoa está (paywall/oferta/checkout) + o plano
// escolhido. Se ela puxar a tela pra baixo (atualizar a página no celular),
// voltar do Stripe ou a página recarregar por qualquer motivo, o funil volta
// exatamente nessa tela — sem obrigar a refazer o quiz inteiro (isso fazia
// perder cliente já na porta do pagamento).
const STAGE_KEY = 'fc_funnel_stage';
const RESTORABLE_STEPS = ['paywall', 'upsell', 'exit_offer', 'checkout'];
function saveStage(step, planId) {
  try {
    sessionStorage.setItem(STAGE_KEY, JSON.stringify({ step, planId: planId ?? null }));
  } catch {
    // sem storage — segue sem guardar
  }
}
function clearStage() {
  try {
    sessionStorage.removeItem(STAGE_KEY);
  } catch {
    // ignora
  }
}
function loadStage() {
  try {
    const s = JSON.parse(sessionStorage.getItem(STAGE_KEY) || 'null');
    if (!s || !RESTORABLE_STEPS.includes(s.step)) return null;
    // precisa ter as respostas (e-mail) guardadas; senão não dá pra restaurar
    const a = loadAnswers();
    if (!a || !a.email_capture) return null;
    if (s.step === 'checkout' && !s.planId) return { step: 'paywall', planId: null };
    return s;
  } catch {
    return null;
  }
}

// ============================================================================
// App.jsx — máquina de estados do funil web, do quiz até o pagamento:
//   quiz (termina no e-mail ou no cupom BR) -> paywall
//   -> (opcional) upsell -> (opcional) exit_offer
//   -> checkout (Stripe) -> checkout_success ("baixe o app").
//
// REMOVIDO em 05/10/2026 (pedido da Gladis): tudo que existia DEPOIS do
// pagamento imitando o app (Home, Treinos, treino ativo, tutorial, aba
// inferior, pedido de avaliação). O funil termina no pagamento; quem
// treina é o app de verdade (App Store / Google Play), com o mesmo
// e-mail. As telas antigas foram arquivadas em _arquivado-pos-pagamento/
// (fora do build, recuperáveis).
//
// Tela de criar conta ("Salve seus dados") REMOVIDA também em 05/10/2026
// (pedido da Gladis: não tem nada a ver com o fluxo). O pagamento usa o
// e-mail capturado na tela de e-mail do funil; a conta com senha é criada
// no app de verdade, com esse mesmo e-mail.
// ============================================================================
export default function App() {
  const [checkoutRedirect] = useState(readCheckoutRedirect);
  // Tela guardada (só vale quando NÃO está voltando do Stripe — nesse caso
  // o ?checkout= manda).
  const [savedStage] = useState(() => (readCheckoutRedirect() ? null : loadStage()));
  const [phase, setPhase] = useState(checkoutRedirect === 'success' ? 'checkout_success' : 'onboarding');
  const [onboardingStep, setOnboardingStep] = useState(
    checkoutRedirect === 'cancel' ? 'paywall' : savedStage?.step ?? 'quiz'
  );
  const [answers, setAnswers] = useState(() => (checkoutRedirect || savedStage ? loadAnswers() : {}));
  // E-mail usado no pagamento (o capturado no funil) — o Checkout precisa
  // dele pra ligar a assinatura à pessoa certa (userId fica vazio: a conta
  // é criada depois, no app).
  const [account, setAccount] = useState(() =>
    checkoutRedirect || savedStage ? { email: loadAnswers().email_capture ?? '', id: '' } : null
  );
  // Qual plano a pessoa escolheu (planId real, ver quizConfig.js) —
  // guardado ao tocar "Continuar" no Paywall/Upsell/Exit.
  const [selectedPlanId, setSelectedPlanId] = useState(savedStage?.planId ?? null);

  // Guarda a tela atual a cada mudança (do paywall em diante); apaga quando
  // o pagamento termina.
  useEffect(() => {
    if (phase === 'checkout_success') {
      clearStage();
    } else if (phase === 'onboarding' && RESTORABLE_STEPS.includes(onboardingStep)) {
      saveStage(onboardingStep, selectedPlanId);
    }
  }, [phase, onboardingStep, selectedPlanId]);

  function startCheckout(planId) {
    setSelectedPlanId(planId);
    setAccount({ email: answers.email_capture ?? '', id: '' });
    setOnboardingStep('checkout');
  }

  const gender = answers.gender ?? 'female';

  // ---- PÁGINA /app (botão do e-mail de boas-vindas) ----------------------
  if (window.location.pathname.replace(/\/+$/, '') === '/app') {
    return <DownloadScreen />;
  }

  // ---- VOLTA DO STRIPE (sucesso) ---------------------------------------
  if (phase === 'checkout_success') {
    return <CheckoutSuccessScreen email={account?.email} />;
  }

  // ---- ONBOARDING -----------------------------------------------------
  if (phase === 'onboarding') {
    switch (onboardingStep) {
      case 'quiz':
        return (
          <QuizFlow
            onComplete={(finalAnswers) => {
              setAnswers(finalAnswers);
              saveAnswers(finalAnswers);
              track('quiz_complete');
              // TODO(api): POST /user_profile com finalAnswers (ver fitclutch_database_schema.sql)
              // Pulado direto pro paywall em 02/10/2026 (pedido da Gladis,
              // conferindo com os screenshots reais do Befit que seguimos a
              // tela toda esse funil): a última tela antes da paywall é o
              // email (mercados fora do BR) ou o cupom (BR) — NÃO existe
              // tela separada de resultado/fitness-score, mapa de prova
              // social nem preview de plano no funil deles. Esse "plano
              // pronto" já está dentro da própria paywall internacional
              // (ver PaywallIntl.jsx, seção do topo com o Dia 1 real).
              // 'results'/'social_proof'/'plan_preview' continuam
              // definidos abaixo e em quizConfig.js, só não são mais
              // visitados nesse fluxo — dá pra reativar fácil se um dia
              // precisar.
              setOnboardingStep('paywall');
            }}
          />
        );

      case 'results':
        return (
          <ResultsScreen
            step={stepById('results')}
            answers={answers}
            gender={gender}
            onNext={() => setOnboardingStep('social_proof')}
          />
        );

      case 'social_proof':
        return (
          <SocialProofMap
            step={stepById('social_proof_map')}
            gender={gender}
            totalUsuarios={1875000} // TODO(api): puxar contagem real de usuários ativos
            onNext={() => setOnboardingStep('plan_preview')}
          />
        );

      case 'plan_preview':
        return (
          <PlanPreviewScreen
            step={stepById('plan_preview')}
            answers={answers}
            gender={gender}
            onNext={() => setOnboardingStep('paywall')}
          />
        );

      case 'paywall':
        return (
          <Paywall
            step={stepById('paywall')}
            gender={gender}
            answers={answers}
            onContinue={(plan) => startCheckout(plan?.planId ?? 'fitclutch_annual')}
            onClose={() => setOnboardingStep('upsell')}
          />
        );

      case 'upsell':
        return (
          <UpsellModal
            step={stepById('upsell_offer')}
            onAccept={() => startCheckout(stepById('upsell_offer').planId)}
            onClose={() => setOnboardingStep('exit_offer')}
          />
        );

      case 'exit_offer':
        return (
          <ExitOffer
            step={stepById('exit_offer')}
            gender={gender}
            onAccept={() => startCheckout(stepById('exit_offer').plan.planId)}
            onDecline={() => setOnboardingStep('paywall')} // sem app dentro do funil: quem recusa a oferta volta pra paywall
          />
        );

      case 'checkout':
        return (
          <CheckoutScreen
            planId={selectedPlanId}
            email={account?.email}
            userId={account?.id}
            onCancel={() => setOnboardingStep('paywall')}
          />
        );

      default:
        return null;
    }
  }

  return null;
}
